// Catalog.ID Client — `export-mail-encrypt`: operational custody of mail_encrypt
// ============================================================================
// The third mail mode (mail migration §5.1, "operational machine custody"). The
// keybag is the ROOT custody and recovery source for the account's
// `mail_encrypt` key; each enrolled machine holds an operational copy, sealed
// under a machine-local KEK on the mail side. That widening of what sits on a
// machine is deliberate and flagged in the plan: mail's per-message content
// keys make receiving a decapsulation per MESSAGE, so chat's "one keybag
// oracle hop per epoch" (`decap-chat-epoch`) does not transfer — a hop per
// message is not viable at mail volume, and the background drain must decrypt
// while the keybag is locked.
//
// THE KEY NEVER TRAVELS IN THE CLEAR. The mail module generates an EPHEMERAL
// hybrid X25519 + ML-KEM-1024 wrap key for this one request and sends the
// public half; the signed canonical bytes commit to its digest. Every
// generation of `mail_encrypt` in the bag is wrapped to that key with
// `kemEncapsulate` under `HKDF_INFO_MAIL_ENCRYPT_EXPORT` and AAD
// `<cid>/mail-encrypt-export/<fingerprint>` — so the blob opens only for the
// machine that made the request, only under this account, only as that
// generation. Retiring generations are exported too: a rotation must not cost
// the ability to read mail sent before it.
//
// WHY THIS MODE HAS A CONSENT DIALOG (and `issue-mail-cert` does not)
//   1. It hands an ACCOUNT PRIVATE KEY to another process. Certification signs
//      one sub-key and persists nothing outside the keybag; this changes what
//      a compromise of the machine yields.
//   2. It is once per machine, not once per 60-day renewal — no click-through
//      treadmill to build.
//   3. The dialog is asked BEFORE the unlock wait (the `create-mail-address`
//      ordering): a user who declines is never made to unlock for a refused
//      act, and the dialog needs nothing from the bag to describe the act.
//
// Gate stack (in order):
//   1–3. sender stamp / requester / active identity — `dispatchMailRequest`.
//   4.   required fields present.
//   5.   `wrapPubkey` is decodable base64.
//   6.   body↔canonical binding: SHA-512(raw wrap key) equals the signed
//        `wrapPubkeyDigestBase64`. Closes the substituted-body gap — and here
//        the substituted body IS the attack (export to the attacker's key).
//   7.   wrap key is EXACTLY 1600 bytes — a hybrid KEM public key.
//   8.   UNLOCK — the universal redirect (M2c): a locked bag raises this tab
//        and waits out the shared budget, ahead of the dialog (a dialog in an
//        unfocused tab is invisible). A no-op when the bag is already open.
//   9.   consent: `deps.confirmMailEncryptExport`; absent ⇒ refuse.
//  10.   `mail_encrypt` is in the bag (any generation).
//  11.   wrap each well-formed generation; zeroize the copies afterwards.
// ============================================================================

import { sha512 } from '@noble/hashes/sha2.js';
import { keys } from '../state/keys';
import { recordKeyUse } from '../state/key-use-record';
import * as aes from '../crypto/aes';
import {
    kemEncapsulate, HKDF_INFO_MAIL_ENCRYPT_EXPORT,
    HYBRID_PUBLIC_KEY_LEN, HYBRID_SECRET_KEY_LEN,
} from '../crypto/kem';
import { deriveKemPublicKey } from './kem-key-chain';
import type {
    MailRequest, MailResponse, MailDispatchDeps, ExportedMailEncryptKey,
} from './mail-types';
import { ensureUnlocked } from './unlock-gate';

/**
 * Keybag slot for the account's mail encryption key (migration §5.1). Hybrid
 * X25519 + ML-KEM-1024, `algorithm: 'X25519-MLKem1024'`, one entry per
 * generation.
 */
export const MAIL_ENCRYPT_KEY_ID = 'mail_encrypt';

/** The label is display text from another process; cap it before rendering. */
const DEVICE_LABEL_MAX_CHARS = 64;

type ResponseBase = Pick<MailResponse, 'type' | 'to' | 'requestId'>;

/**
 * Digest convention shared with the mail service and the relay's C# mirror
 * (`CrossModuleCanonical.ComputeWrapPubkeyDigest`): base64 SHA-512 over the
 * RAW 1600-byte wrap public key, never over its base64 text.
 */
export function computeWrapPubkeyDigest(wrapPubkey: Uint8Array): string {
    return aes.toBase64(sha512(wrapPubkey));
}

/**
 * AAD for one exported generation. Binds the wrap to the account it was
 * exported under and to the generation it carries; the mail side rebuilds it
 * from its own cid and the reported fingerprint (`HubMailKeyExportGateway.Aad`).
 */
export function mailEncryptExportAad(accountCid: string, fingerprintHex: string): Uint8Array {
    return new TextEncoder().encode(`${accountCid}/mail-encrypt-export/${fingerprintHex}`);
}

function unavailable(base: ResponseBase, reason: string): MailResponse {
    return { ...base, status: 'unavailable', reason };
}

export async function handleExportMailEncrypt(
    request: MailRequest,
    base: ResponseBase,
    accountCid: string,
    deps: MailDispatchDeps,
): Promise<MailResponse> {
    // Gate 4.
    if (!request.wrapPubkey || !request.wrapPubkeyDigestBase64) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5.
    let wrapPubkey: Uint8Array;
    try {
        wrapPubkey = aes.fromBase64(request.wrapPubkey);
    } catch {
        return unavailable(base, 'bad-wrap-key-base64');
    }

    // Gate 6: body↔canonical binding.
    if (computeWrapPubkeyDigest(wrapPubkey) !== request.wrapPubkeyDigestBase64) {
        return unavailable(base, 'wrap-key-digest-mismatch');
    }

    // Gate 7.
    if (wrapPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return unavailable(base, 'wrap-key-not-hybrid');
    }

    // Gate 8: the universal unlock redirect (M2c, §10 item 5). It runs BEFORE
    // the dialog because the focus-request is the only thing that brings this
    // tab to the front, and a consent dialog nobody can see is not consent.
    // Asking first used to spare a declining member an unlock; on an unlocked
    // bag the gate is a no-op and that ordering is unchanged.
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    // Gate 9: consent, before the bag is touched.
    if (!deps.confirmMailEncryptExport) {
        // Wired in by `key-grant/client.ts`. A build that cannot ask must not
        // hand out an account private key.
        return unavailable(base, 'consent-ui-unavailable');
    }
    const deviceLabel = typeof request.deviceLabel === 'string' && request.deviceLabel.length > 0
        ? request.deviceLabel.slice(0, DEVICE_LABEL_MAX_CHARS)
        : undefined;
    const decision = await deps.confirmMailEncryptExport({
        owningCid: accountCid,
        requesterId: request.requesterId,
        deviceLabel,
        wrapKeyFingerprint: toHexLower(sha512(wrapPubkey)),
    });
    if (decision !== 'approve') {
        return { ...base, status: 'denied', reason: 'mail-encrypt-export-declined' };
    }

    // Gate 10. `getKeyChain` returns fresh copies of every generation, oldest
    // first; the bag's own notion of "active" is what `getPrivateKey` answers.
    //
    // ABSENT IS NOT LOCKED (device-setup-restructure §1 item 12, §9.2): an
    // unlocked bag with no chain means the member keeps `mail_encrypt` off this
    // device (whitepaper §3.4.7), and this interactive request can load it back
    // — but with `import: 'never'`. This mode is a COPY HAND-OVER: a module
    // asking for the bytes of a key the member deliberately keeps off this
    // device is no reason to start storing it here. The key is opened for this
    // one export and stays out of the device copy.
    let chain = keys.getKeyChain(MAIL_ENCRYPT_KEY_ID);
    if (chain.length === 0) {
        if (request.requiresUserInteraction === true && deps.requireKey) {
            try {
                await deps.requireKey(
                    MAIL_ENCRYPT_KEY_ID, 'give this machine a copy of your mail key',
                    { import: 'never' });
            } catch {
                // A failed load is an absent key; the refusal below says so.
            }
            chain = keys.getKeyChain(MAIL_ENCRYPT_KEY_ID);
        }
        if (chain.length === 0) return unavailable(base, 'key-not-on-device');
    }
    const activeB64 = keys.getPrivateKey(MAIL_ENCRYPT_KEY_ID);

    // Gate 11.
    const exportedKeys: ExportedMailEncryptKey[] = [];
    try {
        for (const entry of chain) {
            if (entry.secretKey.length !== HYBRID_SECRET_KEY_LEN) continue;
            let publicKey: Uint8Array;
            try {
                publicKey = deriveKemPublicKey(entry.secretKey);
            } catch {
                // Malformed key bytes — skipped, as the chain walk skips them.
                continue;
            }
            const fingerprint = toHexLower(sha512(publicKey));
            const wrapped = await kemEncapsulate(
                wrapPubkey, entry.secretKey,
                HKDF_INFO_MAIL_ENCRYPT_EXPORT, mailEncryptExportAad(accountCid, fingerprint));
            exportedKeys.push({
                fingerprint,
                publicKey: aes.toBase64(publicKey),
                wrappedSecretKey: aes.toBase64(wrapped),
                active: activeB64 !== null && aes.toBase64(entry.secretKey) === activeB64,
            });
        }
    } finally {
        // The copies, never the bag's own entries — those stay for the next
        // machine's export and for rotation.
        for (const entry of chain) entry.secretKey.fill(0);
    }

    if (exportedKeys.length === 0) return unavailable(base, 'mail-encrypt-malformed');

    // Evidence of use (§9.4), and the ONE kind that is not an oracle: the key's
    // bytes are now on the mail side under its own KEK. The pane words this as
    // a hand-over for exactly that reason — unticking the key here does not
    // take that copy back (§12).
    recordKeyUse(accountCid, MAIL_ENCRYPT_KEY_ID, {
        requesterId: request.requesterId,
        mode: request.mode,
        kind: 'copy',
    });

    return { ...base, status: 'approved', exportedKeys };
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}
